import { useContext } from 'react'
import { NavLink } from 'libs/router'
import { useTranslation } from 'libs/i18n'

import { ContextMenu, Currency, TableImage } from 'shared/components'
import { Badge, BooleanBadge, Detail } from 'shared/atoms'
import { useUserRoles } from 'shared/hooks'
import { StoreSettingsContext } from 'store'
import { BookingStatus } from 'features/bookings'
import type { ProductProps } from 'features/products/types/products.types'
import { useProductPath } from 'features/products/pages/index/hooks/useProductPath'

export const Product = ({ product, menuOptions }: ProductProps) => {
	const { t } = useTranslation()
	const { isStaff } = useUserRoles()
	const storeCtx = useContext(StoreSettingsContext)
	const productPath = useProductPath()
	const {
		id,
		name,
		sku,
		image_thumbnail,
		price,
		published,
		booking,
		owner,
		category,
		size,
		quantity,
		sum_sold_quantity,
		sum_sold_total,
		regla_synced,
		print_count,
		confirmed
	} = product

	const area = booking?.id
		? `${booking.booth?.booth_type?.name ?? ''}${booking.booth?.number ? ` #${booking.booth.number}` : ''}`.trim() ||
			`#${booking.reference}`
		: '—'

	return (
		<li className="p-4">
			<div className="flex items-start gap-3">
				<TableImage className="h-10 w-10" src={image_thumbnail} />
				<div className="min-w-0 flex-1">
					<NavLink
						className="block truncate text-[13px] font-semibold text-primary hover:underline"
						to={productPath(id)}>
						{name}
					</NavLink>
					<p className="truncate text-[11px] text-muted-foreground">{sku}</p>
				</div>
				<div className="shrink-0 text-right">
					<Currency
						className="block font-display text-[15px] font-bold tabular-nums"
						price={price}
					/>
					<div className="mt-1">
						<Badge uppercase variant={published ? 'success' : 'danger'}>
							{published ? t('Published') : t('Unpublished')}
						</Badge>
					</div>
				</div>
				<ContextMenu
					className="-mr-2 -mt-1"
					dataId={id}
					item={product}
					options={menuOptions}
					label={t('Actions for {{ name }}', { name })}
				/>
			</div>
			<dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
				{isStaff && (
					<Detail
						label={t('Booking status')}
						value={booking?.status ? <BookingStatus status={booking.status} /> : '—'}
					/>
				)}
				{isStaff && <Detail label={t('Owner')} value={owner?.name || owner?.email || '—'} />}
				{storeCtx?.storeSettings?.categories_enabled && (
					<Detail label={t('Category')} value={category?.name || '—'} />
				)}
				<Detail label={t('Size')} value={size || '—'} />
				<Detail label={t('Quantity')} value={quantity} />
				<Detail label={t('Area')} value={area} />
				<Detail label={t('Sold')} value={sum_sold_quantity} />
				<Detail label={t('Total sales')} value={<Currency price={sum_sold_total} />} />
				<div className="col-span-2">
					<dt className="font-medium uppercase text-muted-foreground">{t('Progress')}</dt>
					<dd className="mt-1 flex flex-wrap gap-1.5">
						{isStaff && <BooleanBadge showLabel value={!!regla_synced} label={t('Synced')} />}
						<BooleanBadge showLabel value={!!print_count} label={t('Printed')} />
						{confirmed != null && (
							<BooleanBadge showLabel value={confirmed} label={t('Confirmed')} />
						)}
					</dd>
				</div>
			</dl>
		</li>
	)
}
